import { LoadingOutlined } from '@ant-design/icons';
import styled from '@emotion/styled';
import { useTranslate } from '@tolgee/react';
import { message } from 'antd';
import Tabs from 'antd/lib/tabs';
import { css } from '@emotion/css';
import FeedbackTable from './components/FeedbackTable';
import useCaseList, { CaseStatusType } from './hooks/useCaseList';
import messageSvg from './assets/message.svg';
import useCaseCategory from './hooks/useCaseCategory';
import { useEffect } from 'react';
import { eventBus } from 'services/eventBus';
import { useMemoizedFn } from 'ahooks';
import { CaseListParams } from 'services/api/feedback';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 16px;
  overflow: hidden;
`;

const Banner = styled.div`
  display: flex;
  align-items: center;
  padding: 8px 15px;
  margin-bottom: 16px;
  background: var(--fett-surface-success-bg);
  border-radius: 2px;
  > img {
    flex: 0 0 auto;
    margin-right: 12px;
  }
  > div {
    flex: 1 0 0;
    > span {
      font-size: 12px;
      color: var(--theme-text-2);
    }
  }
`;

const Content = styled.div`
  flex: 1 0 0;
  display: flex;
  flex-direction: column;
  background: var(--fett-bg-panel);
  border-radius: 2px;
  overflow: hidden;
  .${({ theme }) => theme.getAntdPrefixedClassName('tabs')} {
    &-nav {
      margin-bottom: 24px;
    }
    &-nav-wrap {
      margin: 0 24px;
    }
  }
  .${({ theme }) => theme.getAntdPrefixedClassName('table-wrapper')} {
    margin: 0 24px;
  }
`;

function Feedback() {
  const { loading, list, statusType, page, countMap, fetchList } = useCaseList();

  const { t } = useTranslate();

  const { categoryMap } = useCaseCategory();

  const banner = (
    <Banner>
      <img src={messageSvg} alt="message" />
      <div>
        <div>{t('feedback.感谢您的反馈，我们会加快修复进度，在问题修复后第一时间通知您')}</div>
        <span>{t('feedback.问题修复后，会进入已修复，需要您查看并确认问题是否已修复')}</span>
      </div>
    </Banner>
  );

  const refreshList = useMemoizedFn(
    (
      partialParams: Partial<Parameters<typeof fetchList>[0]>,
      feedback?: CaseListParams['feedback'],
    ) => {
      const { current: pageNum = 1, pageSize = 10 } = page;

      return fetchList({
        statusType,
        pageNum,
        pageSize,
        feedback,
        ...partialParams,
      }).catch(() => {
        message.error(t('feedback.获取反馈数据失败，请稍后再试'));
      });
    },
  );

  useEffect(() => {
    const handler = () => {
      refreshList({});
    };

    eventBus.on('CASE_UPDATED', handler);
    return () => {
      eventBus.off('CASE_UPDATED', handler);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!list) {
    if (loading) {
      return (
        <Container>
          {banner}
          <LoadingOutlined />
        </Container>
      );
    }

    return <Container>{banner}</Container>;
  }

  const labelMap: Record<CaseStatusType, string> = {
    all: t('feedback.全部'),
    repairing: t('feedback.修复中'),
    repaired: t('feedback.已修复待确认'),
    closed: t('feedback.已关闭'),
  };

  const tabs = (
    <Tabs
      className={css`
        .ant-tabs-nav-list {
          padding: 5px 0;
        }
      `}
      activeKey={statusType}
      onChange={(statusType) => {
        refreshList({
          statusType: statusType as CaseStatusType,
          pageNum: 1,
        });
      }}
    >
      {Object.keys(countMap).map((key) => {
        const { [key]: count } = countMap;

        const { [key]: label } = labelMap;

        return <Tabs.TabPane key={key} tab={`${label}(${count})`} />;
      })}
    </Tabs>
  );

  const table = (
    <FeedbackTable
      key={statusType}
      loading={loading}
      dataSource={list}
      categoryMap={categoryMap}
      onCheckDetail={(record) => eventBus.emit('SHOW_FEEDBACK_MODAL', record.caseId)}
      pagination={{
        ...page,
        showSizeChanger: true,
        showQuickJumper: true,
        showTotal: (total) => t('feedback.共x项', { x: total }),
      }}
      onChange={({ current: pageNum = 1, pageSize = 10 }, filters) => {
        const feedback = filters?.feedback?.[0] as CaseListParams['feedback'];
        refreshList({
          pageNum,
          pageSize,
          feedback,
        });
      }}
    />
  );

  return (
    <Container>
      {banner}
      <Content>
        {tabs}
        {table}
      </Content>
    </Container>
  );
}

export default Feedback;
